import type { CSSProperties, FC } from 'react';
import type { CabinetCard, SupervisedStudentCabinetCard } from '../types';
import {
  AcademicWorkIcon,
  CalendarIcon,
  ConnectionsIcon,
  FilesIcon,
  LiteratureIcon,
  OfficialIcon,
  OpportunitiesIcon,
  ProjectsIcon,
  RequestsIcon,
  ToolsIcon,
} from './StudentIcons';

export const DEFAULT_STUDENT_CARDS: CabinetCard[] = [
  {
    num: '01',
    title: 'Научная работа под руководством',
    description: 'Тема, индивидуальный план, задачи, написание разделов, НИР, предзащита',
    route_id: 'SCIENTIFIC_WORK',
    icon: 'academic_work',
  },
  {
    num: '02',
    title: 'Мои заявки',
    description: 'Единый реестр личных запросов, опросов, соавторства и услуг',
    route_id: 'MY_REQUESTS',
    icon: 'requests',
  },
  {
    num: '03',
    title: 'Академические связи',
    description: 'Коммуникация с научным руководителем, рецензентами и кафедрой',
    route_id: 'ACADEMIC_CONNECTIONS',
    icon: 'connections',
  },
  {
    num: '04',
    title: 'Найти литературу',
    description: '9-параметрический поиск по базам Scopus, WoS, ВАК и библиотеке',
    route_id: 'FIND_LITERATURE',
    icon: 'literature',
  },
  {
    num: '05',
    title: 'Программы и инструменты',
    description: 'Каталог 36 инструментов анализа данных, конструктор опросов и протоколы',
    route_id: 'PROGRAMS_AND_TOOLS',
    icon: 'tools',
  },
  {
    num: '06',
    title: 'Научные проекты',
    description: 'Совместный исследовательский проект с руководителем (8 разделов)',
    route_id: 'RESEARCH_PROJECTS',
    icon: 'projects',
  },
  {
    num: '07',
    title: 'Возможности и развитие',
    description: 'Гранты, стипендии, конкурсы, школы и академическая мобильность',
    route_id: 'OPPORTUNITIES',
    icon: 'opportunities',
  },
  {
    num: '08',
    title: 'Встречи и дежурства',
    description: 'Консультации с научным руководителем и кафедральные дежурства',
    route_id: 'MEETINGS_AND_DUTIES',
    icon: 'calendar',
  },
  {
    num: '09',
    title: 'Файлы и документы',
    description: 'Архив рукописей, датасетов, справок и презентаций студента',
    route_id: 'FILES_AND_DOCUMENTS',
    icon: 'files',
  },
  {
    num: '10',
    title: 'Официальные источники ENU',
    description: 'Верифицированный каталог информационных ресурсов и регламентов университета',
    route_id: 'ENU_SOURCES',
    icon: 'official',
  },
];

const ACCENTS = [
  '#e2ad3a',
  '#41d2ff',
  '#49ddb7',
  '#9e80ff',
  '#ff7a64',
  '#5da8ff',
  '#66b7e8',
  '#e2ad3a',
  '#41d2ff',
  '#49ddb7',
];

interface StudentCabinetGridProps {
  cards?: CabinetCard[];
  onSelectCard: (card: SupervisedStudentCabinetCard | any) => void;
}

export const StudentCabinetGrid: FC<StudentCabinetGridProps> = ({
  cards = DEFAULT_STUDENT_CARDS,
  onSelectCard,
}) => {
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'academic_work':
        return <AcademicWorkIcon className="w-5 h-5" />;
      case 'requests':
        return <RequestsIcon className="w-5 h-5" />;
      case 'connections':
        return <ConnectionsIcon className="w-5 h-5" />;
      case 'literature':
        return <LiteratureIcon className="w-5 h-5" />;
      case 'tools':
        return <ToolsIcon className="w-5 h-5" />;
      case 'projects':
        return <ProjectsIcon className="w-5 h-5" />;
      case 'opportunities':
        return <OpportunitiesIcon className="w-5 h-5" />;
      case 'calendar':
        return <CalendarIcon className="w-5 h-5" />;
      case 'files':
        return <FilesIcon className="w-5 h-5" />;
      case 'official':
        return <OfficialIcon className="w-5 h-5" />;
      default:
        return <AcademicWorkIcon className="w-5 h-5" />;
    }
  };

  return (
    <div className="space-y-4">
      <div className="owner-hub-sections-head">
        <div>
          <h3>Разделы кабинета</h3>
          <small>10 основных разделов научной деятельности обучающегося под руководством</small>
        </div>
      </div>

      <div className="owner-hub-grid">
        {cards.map((card, idx) => (
          <button
            key={card.num}
            type="button"
            onClick={() => onSelectCard(card.route_id || card)}
            className="owner-hub-card"
            style={{ '--action': ACCENTS[idx % ACCENTS.length] } as CSSProperties}
          >
            <span className="owner-hub-index">{card.num}</span>
            <span className="owner-hub-icon" aria-hidden="true">
              {getIcon(card.icon)}
            </span>
            <span className="owner-hub-copy">
              <strong>{card.title}</strong>
              <span>
                {card.description}
                {card.badge ? ` · ${card.badge}` : ''}
              </span>
            </span>
            <span className="owner-hub-arrow" aria-hidden="true">
              ›
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};
